<template>
  <!--
    Task 11 / STEP-12 (Q-011): destination follows ACTING CONTEXT, not
    membership. A dual-role user who has not chosen yet is asked rather than
    sent somewhere arbitrary -- picking for them would silently decide which
    half of their account they came here to use.
  -->
  <!--
    Task 12 / STEP-04: two equal-weight choice cards -- the choice IS the
    action, so neither is a primary CTA and each carries a one-line description
    of what that role does.

    DESIGN-03: the accepted student-foundation visual language -- a dedicated
    centered auth-like shell (the normal dashboard header/sidebar are
    suppressed while this gate is up; see the `roleSelectionRequired` watcher),
    an in-card Borna lockup, and iconed indigo (student) / teal
    (volunteer) cards. Behaviour is unchanged: a click runs the existing
    `chooseRole` -> `setActiveRole` path, the dashboard swaps in reactively at
    the same URL, and nothing about identity, auth, membership, routing or the
    volunteer/teacher experience changes. There is no selected state and no
    Continue button.
  -->
  <div
    v-if="roleSelectionRequired"
    class="role-gate"
    data-testid="role-selection-required"
  >
    <div class="role-gate__orb role-gate__orb--indigo" aria-hidden="true"></div>
    <div class="role-gate__orb role-gate__orb--teal" aria-hidden="true"></div>

    <section class="role-gate__card">
      <div class="role-gate__brand">
        <img class="role-gate__logo" :src="bornaLogoUrl" alt="برنا" />
        <span class="role-gate__tagline">
          {{ $t('dashboard.roleSelection.tagline') }}
        </span>
      </div>

      <div class="role-gate__copy">
        <span class="role-gate__eyebrow">
          {{ $t('dashboard.roleSelection.eyebrow') }}
        </span>
        <h1 class="role-gate__heading">
          {{ $t('dashboard.roleSelection.header') }}
        </h1>
        <p class="role-gate__body">
          {{ $t('dashboard.roleSelection.body') }}
        </p>
      </div>

      <div class="role-gate__choices">
        <button
          type="button"
          class="role-choice role-choice--student"
          data-testid="role-selection-student"
          :disabled="isSwitchingRole"
          @click="chooseRole('student')"
        >
          <span class="role-choice__icon">
            <svg
              viewBox="0 0 24 24"
              width="26"
              height="26"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M4 5.5A3.5 3.5 0 0 1 7.5 2H12v18H7.5A3.5 3.5 0 0 0 4 23Z"
              />
              <path
                d="M20 5.5A3.5 3.5 0 0 0 16.5 2H12v18h4.5a3.5 3.5 0 0 1 3.5 3Z"
              />
            </svg>
          </span>
          <span class="role-choice__text">
            <span class="role-choice__label">
              {{ $t('dashboard.roleSelection.student') }}
            </span>
            <span class="role-choice__hint">
              {{ $t('dashboard.roleSelection.studentHint') }}
            </span>
          </span>
          <span class="role-choice__arrow">
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </span>
        </button>

        <button
          type="button"
          class="role-choice role-choice--volunteer"
          data-testid="role-selection-volunteer"
          :disabled="isSwitchingRole"
          @click="chooseRole('volunteer')"
        >
          <span class="role-choice__icon">
            <svg
              viewBox="0 0 24 24"
              width="26"
              height="26"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <circle cx="9" cy="8" r="4" />
              <path d="M2 21a7 7 0 0 1 14 0M17 8h5M19.5 5.5v5" />
            </svg>
          </span>
          <span class="role-choice__text">
            <span class="role-choice__label">
              {{ $t('dashboard.roleSelection.volunteer') }}
            </span>
            <span class="role-choice__hint">
              {{ $t('dashboard.roleSelection.volunteerHint') }}
            </span>
          </span>
          <span class="role-choice__arrow">
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </span>
        </button>
      </div>

      <p class="role-gate__note" data-testid="role-selection-note">
        <svg
          class="role-gate__note-icon"
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M12 3 20 6v6c0 5-3.4 8.3-8 9-4.6-.7-8-4-8-9V6Z" />
          <path d="m9 12 2 2 4-4" />
        </svg>
        {{ $t('dashboard.roleSelection.note') }}
      </p>
    </section>
  </div>
  <div v-else>
    <!--
      Volunteer/teacher: ABSOLUTE FREEZE (P3 full-shell remediation,
      00_*.md §13.2, PF-13). The role bar is shown only to an identity that
      genuinely holds both roles, so single-role volunteers see exactly what
      they saw before. Unchanged since Task 12 / STEP-04 (B, C).
    -->
    <div
      v-if="isVolunteer && isDualRole"
      class="role-bar"
      data-testid="role-bar"
    >
      <p class="role-bar__current" data-testid="active-role-indicator">
        {{ activeRoleLabel }}
      </p>
      <button
        type="button"
        class="uc-link-button role-bar__switch"
        data-testid="role-switcher"
        :disabled="isSwitchingRole"
        @click="chooseRole(inactiveRole)"
      >
        {{ switchRoleLabel }}
      </button>
    </div>

    <!--
      P3 full-shell visual remediation (00_*.md §13): student-only, replaces
      the legacy full-width role strip with the rendered prototype's own
      header structure. The real role-switch capability is preserved for a
      dual-role student -- only its container changes; single-role students
      simply don't get the button (`showRoleSwitch` is `isDualRole`, same
      condition the old `.role-bar` used).
    -->
    <student-app-header
      v-if="!isVolunteer"
      :title="$t('appSidebar.sidebarLinks.studentHome')"
      :show-role-switch="isDualRole"
      :switch-to-label="$t('dashboard.roleSwitcher.volunteerRoleName')"
      :disabled="isSwitchingRole"
      :switch-coming-soon="teacherSwitchComingSoon"
      :ai-tutor-credits-remaining="aiTutorCreditsRemaining"
      @switch-role="onSwitchRole"
    />

    <volunteer-dashboard v-if="isVolunteer" />
    <student-dashboard v-else />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

import VolunteerDashboard from './VolunteerDashboard/index.vue'
import StudentDashboard from './StudentDashboard/index.vue'
import StudentAppHeader from '@/components/StudentShell/StudentAppHeader.vue'
import BornaLogoUrl from '@/assets/logo/borna-identity.png'

export default {
  name: 'dashboard-view',
  components: { VolunteerDashboard, StudentDashboard, StudentAppHeader },
  data() {
    return { isSwitchingRole: false, bornaLogoUrl: BornaLogoUrl }
  },
  // DESIGN-03. The acting-context gate is a full-screen decision, not an
  // in-dashboard card, so while `roleSelectionRequired` is true the normal
  // dashboard header + sidebar are suppressed. The moment a role is chosen the
  // getter flips false, the chrome is handed straight back, and the child
  // dashboard's own header lifecycle refines it. Presentation only -- no
  // route, guard, API or role-model change.
  watch: {
    roleSelectionRequired: {
      immediate: true,
      handler(gated, wasGated) {
        if (gated) this.$store.dispatch('app/hideNavigation')
        else if (wasGated) this.$store.dispatch('app/showNavigation')
      },
    },
  },
  beforeDestroy() {
    if (this.roleSelectionRequired) this.$store.dispatch('app/showNavigation')
  },
  methods: {
    // Switching acting context only -- no identity, role, profile or
    // authentication change, and deliberately no re-login.
    async chooseRole(role) {
      if (this.isSwitchingRole) return
      this.isSwitchingRole = true
      try {
        await this.$store.dispatch('user/setActiveRole', role)
      } finally {
        this.isSwitchingRole = false
      }
    },
    // STUDENT-UX-02 / UX2-02 (doc 30 §3, D-3; named-scope supersession of
    // doc 22 §5.2): while the Student teacher capability is OFF the header's
    // "switch to teacher" option opens the SAME SX-02 Coming Soon dialog the
    // Home teacher card uses -- no acting-context change, no
    // `user/setActiveRole`, no role-change request, no navigation. With the
    // capability ON (and for the volunteer-side switch, which never comes
    // through here) the real switch runs exactly as before.
    onSwitchRole() {
      if (this.teacherSwitchComingSoon) {
        this.$store.dispatch('app/comingSoon/show', {
          variant: 'teacher',
          reason: 'register',
        })
        return
      }
      this.chooseRole(this.inactiveRole)
    },
  },
  computed: {
    ...mapState({
      user: state => state.user.user,
    }),
    ...mapGetters({
      isAuthenticated: 'user/isAuthenticated',
      isVolunteer: 'user/isVolunteer',
      isDualRole: 'user/isDualRole',
      roleSelectionRequired: 'user/roleSelectionRequired',
      // SX-02: the ONE central teacher capability (doc 22 §4); a missing
      // getter degrades to `undefined` = OFF.
      teacherExperienceAvailable:
        'featureFlags/isStudentTeacherExperienceAvailable',
    }),
    // UX2-02: the Student-side switch targets the teacher role, which is not
    // available to Students while the capability is OFF.
    teacherSwitchComingSoon() {
      return (
        this.inactiveRole === 'volunteer' &&
        this.teacherExperienceAvailable !== true
      )
    },
    // CR-03 (doc 28 §16/§17): Subway's Student AI Tutor credit balance for the
    // header pill; null (unknown / not a Student) hides it.
    aiTutorCreditsRemaining() {
      const credits = this.$store.getters['user/aiTutorCreditsRemaining']
      return typeof credits === 'number' ? credits : null
    },

    // Task 12 / STEP-04. Derived from `isVolunteer` -- the frozen acting-context
    // alias -- rather than from a new getter, so the role model is unchanged.
    // This branch only renders once a role has been chosen, so acting context
    // is never null here.
    inactiveRole() {
      return this.isVolunteer ? 'student' : 'volunteer'
    },

    activeRoleLabel() {
      return this.isVolunteer
        ? this.$t('dashboard.roleSwitcher.usingAsVolunteer')
        : this.$t('dashboard.roleSwitcher.usingAsStudent')
    },

    // Names the DESTINATION role, so the pair reads as
    // "Using Borna as Student" / "Switch to Tutor / Volunteer".
    switchRoleLabel() {
      return this.isVolunteer
        ? this.$t('dashboard.roleSwitcher.toStudent')
        : this.$t('dashboard.roleSwitcher.toVolunteer')
    },
  },
}
</script>

<!--
  DESIGN-03. Dedicated centered auth-like shell for the acting-context gate,
  following the accepted student-foundation prototype. Scoped to this view --
  the shared login shell (FormPageTemplate) and the dashboards are untouched.
  The local indigo / teal / ink values mirror the prototype palette; they are
  not promoted to global tokens because nothing else uses them yet.
-->
<style lang="scss" scoped>
$rg-ink: #071447;
$rg-canvas: #f5f7fc;
$rg-card-border: #e2e8f0;
$rg-indigo-50: #eef2ff;
$rg-indigo-100: #dbe4ff;
$rg-indigo-300: #91a5ff;
$rg-indigo-600: #314bd6;
$rg-indigo-700: #243ab4;
$rg-teal-50: #e9f9fd;
$rg-teal-100: #cdf1f8;
$rg-teal-700: #087d9e;

// --- Acting-context gate: shell ------------------------------------------------
.role-gate {
  align-items: center;
  background: radial-gradient(
      circle at 18% 20%,
      rgba(49, 75, 214, 0.09),
      transparent 28%
    ),
    radial-gradient(circle at 84% 84%, rgba(20, 183, 215, 0.1), transparent 30%),
    $rg-canvas;
  display: flex;
  justify-content: center;
  min-height: 100vh;
  overflow: hidden;
  padding: 64px 22px 40px;
  position: relative;

  @include breakpoint-below('small') {
    align-items: flex-start;
    padding-top: 40px;
  }
}

.role-gate__orb {
  border-radius: 50%;
  filter: blur(1px);
  pointer-events: none;
  position: absolute;
}

.role-gate__orb--indigo {
  background: $rg-indigo-100;
  height: 240px;
  left: -100px;
  top: -80px;
  width: 240px;
}

.role-gate__orb--teal {
  background: $rg-teal-100;
  bottom: -110px;
  height: 280px;
  right: -150px;
  width: 280px;
}

// --- Card --------------------------------------------------------------------
.role-gate__card {
  background: white;
  border: 1px solid $rg-card-border;
  border-radius: 32px;
  box-shadow: 0 18px 50px rgba(7, 20, 71, 0.08);
  max-width: 460px;
  padding: 36px;
  position: relative;
  width: 100%;
  z-index: 1;

  @include breakpoint-below('small') {
    border-radius: 24px;
    padding: 26px 20px;
  }
}

// --- In-card Borna lockup ----------------------------------------------------
.role-gate__brand {
  align-items: center;
  border-bottom: 1px solid $c-border-grey;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0 auto 22px;
  max-width: 260px;
  padding-bottom: 16px;
}

.role-gate__logo {
  display: block;
  height: 72px;
  object-fit: contain;
  width: auto;

  @include breakpoint-below('small') {
    height: 64px;
  }
}

.role-gate__tagline {
  color: $rg-indigo-700;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
}

// --- Copy ------------------------------------------------------------------
.role-gate__copy {
  margin-bottom: 20px;
  text-align: center;
}

.role-gate__eyebrow {
  color: $c-information-blue;
  display: block;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.role-gate__heading {
  color: $rg-ink;
  font-size: clamp(23px, 3.4vw, 28px);
  font-weight: 600;
  line-height: 1.4;
  margin: 8px 0 10px;
}

.role-gate__body {
  color: $c-secondary-grey;
  font-size: 14px;
  line-height: 1.7;
  margin: 0;
}

// --- Equal-weight choice cards --------------------------------------------
// The choice IS the action, so neither card is a primary CTA. Selection calls
// the existing `chooseRole` -> `setActiveRole` path directly; there is no
// intermediate selected state and no Continue button.
.role-gate__choices {
  display: grid;
  gap: 12px;
}

.role-choice {
  align-items: center;
  background: white;
  border: 1.5px solid $rg-card-border;
  border-radius: 20px;
  cursor: pointer;
  display: grid;
  gap: 13px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  min-height: 92px;
  padding: 15px;
  text-align: start;
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    transform 0.18s ease;
  width: 100%;

  &:hover {
    box-shadow: 0 12px 28px rgba(7, 20, 71, 0.09);
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 2px solid $c-information-blue;
    outline-offset: 2px;
  }

  &:disabled {
    box-shadow: none;
    cursor: default;
    opacity: 0.6;
    transform: none;
  }
}

.role-choice--student {
  &:hover {
    border-color: $rg-indigo-300;
  }

  .role-choice__icon {
    background: $rg-indigo-50;
    color: $rg-indigo-600;
  }

  .role-choice__arrow {
    color: $rg-indigo-600;
  }
}

.role-choice--volunteer {
  &:hover {
    border-color: rgba(20, 183, 215, 0.55);
  }

  .role-choice__icon {
    background: $rg-teal-50;
    color: $rg-teal-700;
  }

  .role-choice__arrow {
    color: $rg-teal-700;
  }
}

.role-choice__icon {
  align-items: center;
  border-radius: 16px;
  display: flex;
  flex: 0 0 auto;
  height: 52px;
  justify-content: center;
  width: 52px;
}

.role-choice__text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.role-choice__label {
  color: $rg-ink;
  font-size: 15px;
  font-weight: 600;
}

.role-choice__hint {
  color: $c-secondary-grey;
  font-size: 12.5px;
  line-height: 1.6;
}

.role-choice__arrow {
  align-items: center;
  background: $rg-canvas;
  border-radius: 10px;
  display: flex;
  flex: 0 0 auto;
  height: 30px;
  justify-content: center;
  width: 30px;
}

// --- Switch-anytime reassurance ----------------------------------------------
.role-gate__note {
  align-items: center;
  color: $c-secondary-grey;
  display: flex;
  font-size: 12px;
  gap: 7px;
  justify-content: center;
  line-height: 1.6;
  margin: 16px 0 0;
  text-align: center;
}

.role-gate__note-icon {
  color: $rg-teal-700;
  flex: 0 0 auto;
}

// --- Direction ------------------------------------------------------------
// `html.is-rtl * { direction: rtl }` (App/index.vue) already mirrors the card
// grid, so the icon and arrow columns swap sides on their own. Only the
// forward-arrow glyph and the decorative orbs need an explicit physical flip.
html.is-rtl {
  .role-choice__arrow svg {
    transform: scaleX(-1);
  }

  .role-gate__orb--indigo {
    left: auto;
    right: -100px;
  }

  .role-gate__orb--teal {
    left: -150px;
    right: auto;
  }
}

// --- Active-role bar -------------------------------------------------------
// Aligned with the dashboards' own content padding (40px 20px / 40px) so the
// bar reads as part of the page rather than floating above it.
.role-bar {
  align-items: baseline;
  border-bottom: 1px solid $c-border-grey;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 16px 20px;

  @include breakpoint-above('medium') {
    padding: 16px 40px;
  }
}

// The acting context is stated in words, never by colour or icon alone.
.role-bar__current {
  color: $c-soft-black;
  font-size: 14px;
  font-weight: 500;
  // Keeps the switch action on the same row until the label genuinely runs out
  // of space, at which point flex-wrap drops it to its own line. Physical
  // margins rather than `margin-inline-end`, mirrored under `html.is-rtl`
  // below: the logical property needs Safari 14.1, and this is a mobile-first
  // product.
  margin: 0 12px 0 0;
}

.role-bar__switch {
  font-size: 14px;
}

// --- Direction -------------------------------------------------------------
// `html.is-rtl * { direction: rtl }` in App/index.vue already mirrors the flex
// row itself; only the physical margin above needs flipping. Nesting under
// `html.is-rtl` is the pattern used by the signup and login views.
html.is-rtl {
  .role-bar__current {
    margin: 0 0 0 12px;
  }
}
</style>
